"use client";

import { useRef } from "react";
import Link from "next/link";
import Image from "next/image";
import { Reveal } from "@/components/ui/Reveal";

export type FastTrackCard = {
  title: string;
  href: string;
  image: string;
  tag?: string;
};

const Chevron = ({ dir }: { dir: "left" | "right" }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className={dir === "left" ? "rotate-180" : ""}
  >
    <path d="M9 6l6 6-6 6" />
  </svg>
);

/**
 * Client carousel for the fast-track. Receives a pre-built, lightweight card
 * list from the server component so the full treatments data never reaches
 * the client bundle.
 */
export function FastTrackCarousel({ cards }: { cards: FastTrackCard[] }) {
  const scrollerRef = useRef<HTMLDivElement>(null);

  const scroll = (dir: 1 | -1) => {
    const el = scrollerRef.current;
    if (!el) return;
    // One near-viewport of cards, clamped by the browser at the ends.
    el.scrollBy({
      left: dir * Math.min(el.clientWidth * 0.8, 900),
      behavior: "smooth",
    });
  };

  return (
    <div className="mt-16 border-t silk-hairline pt-12 sm:mt-20 sm:pt-16 lg:mt-24">
      {/* Header (contained): eyebrow + heading. The view-all link and scroll
          arrows sit below the rail (see footer). */}
      <div className="mb-8 sm:mb-10">
        <Reveal variant="fade">
          <p className="silk-eyebrow">
            <span className="silk-eyebrow-num">·</span>
            <span className="silk-eyebrow-rule" />
            <span>Start Here</span>
          </p>
          <h2 className="mt-4 silk-display-3 text-ink">
            Treatments,{" "}
            <em className="italic text-gold-bright">considered</em>.
          </h2>
          <p className="silk-body mt-4 max-w-md text-ink/70">
            From non-surgical skin tightening to cosmetic injectables, our
            nurse-led clinic brings specialist training and clinical rigour to
            every plan. We refuse the obvious in favour of what holds up.
          </p>
        </Reveal>
      </div>

      {/* Carousel: first card aligns with the heading, the rail bleeds off
          the right edge to the viewport for a full-width feel. The negative
          right margin (calc(50% - 50vw)) extends the scroll area to the page
          edge at any width; the right padding gives the last card breathing
          room. Scrollbar hidden. */}
      <div className="mr-[calc(50%-50vw)]">
        <div
          ref={scrollerRef}
          className="flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth pb-2 pr-5 sm:gap-5 sm:pr-8 lg:pr-16 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {cards.map((card) => (
            <Link
              key={card.href}
              href={card.href}
              className="group block w-[220px] shrink-0 snap-start sm:w-[248px] lg:w-[268px]"
            >
              <div className="relative aspect-[3/4] overflow-hidden rounded-2xl bg-oat">
                <Image
                  src={card.image}
                  alt={card.title}
                  fill
                  className="object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.05]"
                  sizes="(max-width: 640px) 220px, 268px"
                />
                <div
                  aria-hidden="true"
                  className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/25 to-transparent"
                />
                <div className="absolute inset-x-0 bottom-0 p-4">
                  {card.tag && (
                    <span className="mb-1.5 block text-[10px] uppercase tracking-[0.2em] text-gold-bright">
                      {card.tag}
                    </span>
                  )}
                  <h3 className="font-display text-[16px] leading-tight text-ivory transition-colors group-hover:text-gold-bright">
                    {card.title}
                  </h3>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Controls below the rail (contained): view-all link + scroll arrows */}
      <Reveal variant="fade" delay={120}>
        <div className="mt-8 flex items-center justify-between gap-3 sm:mt-10 lg:grid lg:grid-cols-3">
          {/* Arrows: centred in the page on desktop, left on mobile/tablet */}
          <div className="flex items-center gap-2 lg:col-start-2 lg:justify-self-center">
            <button
              type="button"
              onClick={() => scroll(-1)}
              aria-label="Scroll treatments left"
              className="flex h-11 w-11 items-center justify-center rounded-full border border-ink/20 text-ink/70 transition-colors hover:border-gold hover:text-gold-deep"
            >
              <Chevron dir="left" />
            </button>
            <button
              type="button"
              onClick={() => scroll(1)}
              aria-label="Scroll treatments right"
              className="flex h-11 w-11 items-center justify-center rounded-full border border-ink/20 text-ink/70 transition-colors hover:border-gold hover:text-gold-deep"
            >
              <Chevron dir="right" />
            </button>
          </div>
          {/* View all: right */}
          <Link
            href="/treatments"
            className="silk-pill silk-pill--secondary shrink-0 lg:col-start-3 lg:justify-self-end"
          >
            View all treatments
          </Link>
        </div>
      </Reveal>
    </div>
  );
}
